import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { FormProvider, useForm } from 'react-hook-form';
import { useAtom, useSetAtom } from 'jotai';
import useInfiniteQuery from '../../../Hooks/useInfiniteQuery';
import useAPI from '../../../api';
import TemplateMessageDialog from '../../../Components/Dialog/TemplateMessageDialog';
import MassiveSmsWrapper from '../MassiveDialog/MassiveSmsWrapper';
import CustomMassiveActions from '../MassiveDialog/Inputs/CustomMassiveActions';
import { openSnackbarAdviseAtom, searchValuesAtom } from '../../../Atoms';
import SectionPage from '../../../Components/Paper/SectionPage';
import CampaignAccordion from './CampaignAccordion';
import groupTemplatesByName from '../../../Helpers/Arrays/groupTemplatesByName';
import mapFilters from '../../../Helpers/Filters/mapFilters';
import { formatPayloadDate } from '../../../Helpers/dates';
import CampaignsFilters from './CampaignsFilters';
import {
  CAMPAIGN_FILTERS,
  CAMPAIGN_RANGE_FILTERS,
  CAMPAIGN_SORTING,
} from '../Utils/constants';

const campaignEntry = (
  item,
  goToMassiveAnalysis,
  handleDeleteMassiveSMS,
  expanded,
  isSupervisor,
  templates,
  toggleAccordion
) => (
  <CampaignAccordion
    key={item?.id}
    isSupervisor={isSupervisor}
    templateName={templates?.find(({ id }) => id === item?.templateId)?.title}
    data={item}
    handleDeleteMassiveSMS={handleDeleteMassiveSMS}
    handleGoToMassiveAnalysis={goToMassiveAnalysis}
    toggleAccordion={toggleAccordion}
    expanded={expanded}
  />
);

//whole days, end inclusive
const toPayloadRange = (range) => {
  const [start, end] = range || [];
  if (!start || !end) return null;
  const endOfDay = new Date(end);
  endOfDay.setHours(23, 59, 59, 999);
  return [
    formatPayloadDate(start).toISOString(),
    formatPayloadDate(endOfDay).toISOString(),
  ];
};

const totalCustomersInvolved = (sourceArray) =>
  sourceArray.reduce((acc, curr) => acc + (curr?.involvedCustomers || 0), 0);

const MassiveSMSComponents = function MassiveSMSComponents({
  handleChangeSection,
  userRoles = [],
}) {
  const API = useAPI();
  const intl = useIntl();
  const methods = useForm();

  const isSupervisor = Boolean(
    userRoles?.find(
      ({ roleId }) =>
        roleId === 'ADMIN' || roleId === 'SUPERVISOR' || roleId === 'SUPERADMIN'
    )
  );

  const setOpenSnackbarAdvise = useSetAtom(openSnackbarAdviseAtom);
  const [searchValues, setSearchValues] = useAtom(searchValuesAtom);

  const [newMassive, setNewMassive] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState(null);
  const [dialogPage, setDialogPage] = useState(0);
  const [customersInvolvedCounts, setCustomersInvolvedCounts] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [waveSections, setWaveSections] = useState([]);

  const toggleAccordion = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleOpenMassiveSMSDialog = () => {
    setNewMassive(true);
  };

  const handleCloseMassiveSMSDialog = () => {
    setSelectedFilters(null);
    setNewMassive(false);
    setDialogPage(0);
  };
  const handleChangeDialogPage = (number) => {
    const newPage = dialogPage + number;

    setDialogPage(newPage);
  };
  const handleSelectNewFilters = (filtersObject) => {
    console.log('selected', filtersObject);
    setSelectedFilters(filtersObject);
  };

  const handleChangeFilters = (newFilters) => {
    setSearchValues((prev) => ({ ...prev, ...newFilters }));
  };

  const getMassiveSMSHistory = useCallback(
    (pageNumber) =>
      API.getMassiveSMSHistory({
        pageNumber,
        filterExpression: mapFilters(
          ['name'],
          CAMPAIGN_FILTERS,
          CAMPAIGN_RANGE_FILTERS,
          {
            ...searchValues,
            scheduledOn: toPayloadRange(searchValues?.scheduledOn),
          }
        ),
        sortExpression: searchValues?.sortValue
          ? {
              sortValue: searchValues?.sortValue.value,
              sort: searchValues?.sortValue.direction,
            }
          : null,
      }),
    [searchValues]
  );
  const {
    ObserverWrapper,
    setNewItems,
    items: campaignsList,
    recordCount,
    updatePropertyValue: updateCustomersInvolved,
  } = useInfiniteQuery(getMassiveSMSHistory);

  useEffect(() => {
    API.getSectionTemplates(
      'MASSIVE',
      { silent: true },
      (result) => {
        setTemplates(result);
      },
      () => {
        setOpenSnackbarAdvise({
          message: intl.messages.MASSIVE_TEMPLATES_ERROR,
          severity: 'error',
        });
      }
    );
  }, []);

  useEffect(() => {
    API.getWaveSections(
      (res) => {
        setWaveSections(res);
      },
      () => {
        setWaveSections([]);
      }
    );
  }, []);

  useEffect(() => {
    if (selectedFilters) {
      const payload = { ...selectedFilters };
      if (payload?.scheduledOn) delete payload.scheduledOn;
      if (payload?.templateId) delete payload.templateId;

      API.getMassiveSMSPreview(
        payload,
        (response) => {
          setCustomersInvolvedCounts(response);
          handleChangeDialogPage(1);
        },
        () => {
          setOpenSnackbarAdvise({
            message: intl.messages.MASSIVE_PREVIEW_ERROR,
            severity: 'error',
          });
        }
      );
    }
  }, [selectedFilters]);

  const handleSendMassiveSMS = (massiveObj) => {
    const { templateId, scheduledOn, campaignEndsOn, campaignName, ...others } =
      massiveObj;

    const payload = {
      campaign: {
        name: campaignName,
        templateId,
        scheduledOn: scheduledOn.toISOString(),
        endsOn: campaignEndsOn.toISOString(),
        customersInvolved: totalCustomersInvolved(customersInvolvedCounts), //somma dei valori dell'array di branches
      },
      filters: others,
    };

    API.sendMassiveSMS(
      payload,
      (result) => {
        if (result) setNewItems(result, recordCount + 1);
        setOpenSnackbarAdvise({
          message: intl.messages.MASSIVE_CREATED_SUCCESSFULLY,
          severity: 'success',
        });
        handleCloseMassiveSMSDialog();
      },
      () => {
        setOpenSnackbarAdvise({
          message: intl.messages.MASSIVE_CREATION_ERROR,
          severity: 'error',
        });
      }
    );
  };

  const handleDeleteMassiveSMS = (activityId, campaignId) => {
    API.deleteMassiveSMS(
      activityId,
      { silent: true },
      () => {
        const smses = campaignsList.find(
          ({ id }) => id === campaignId
        )?.massiveSmses;
        const index = smses?.findIndex(
          ({ massiveId }) => massiveId === activityId
        );
        smses?.splice(index, 1);
        updateCustomersInvolved(campaignId, 'massiveSmses', smses);
        setOpenSnackbarAdvise({
          message: intl.messages.MASSIVE_DELETED_SUCCESSFULLY,
          severity: 'success',
        });
      },
      () => {
        setOpenSnackbarAdvise({
          message: intl.messages.MASSIVE_DELETION_ERROR,
          severity: 'error',
        });
      }
    );
  };

  const goToMassiveAnalysis = (
    campaignName,
    branchId = null,
    activityType = null
  ) => {
    handleChangeSection('/sendsAnalysis', {
      campaignName,
      branchId: branchId ? [branchId] : null,
      activityType: activityType ? [activityType] : null,
    });
  };

  return (
    <>
      <SectionPage
        hideExportExcel
        sortingOptions={CAMPAIGN_SORTING}
        customFilters={
          <CampaignsFilters
            defaultValue={searchValues}
            onConfirm={handleChangeFilters}
          />
        }
        fab={isSupervisor ? intl.messages.NEW_MASSIVE : null}
        onClickFab={isSupervisor ? handleOpenMassiveSMSDialog : null}
        sectionName="MASSIVE_SENDING"
        customEntryFunction={(item) =>
          campaignEntry(
            item,
            goToMassiveAnalysis,
            handleDeleteMassiveSMS,
            expanded,
            isSupervisor,
            templates,
            toggleAccordion
          )
        }
        ListObserverWrapper={ObserverWrapper}
      />
      <FormProvider {...methods}>
        <TemplateMessageDialog
          id="massive-sms"
          open={newMassive}
          handleClose={handleCloseMassiveSMSDialog}
          title={
            dialogPage === 0
              ? intl.messages.FILTER_CUSTOMERS
              : intl.messages.SEND_MASSIVE
          }
          handleSubmit={
            dialogPage === 1 ? handleSendMassiveSMS : handleSelectNewFilters
          }
          actions={
            <CustomMassiveActions
              close={handleCloseMassiveSMSDialog}
              goBack={() => handleChangeDialogPage(-1)}
              currentPage={dialogPage}
            />
          }
          widgetWrapper={
            <MassiveSmsWrapper
              templates={groupTemplatesByName(templates)}
              selectedFilters={selectedFilters}
              currentPage={dialogPage}
              customersInvolvedCounts={customersInvolvedCounts}
              waveSections={waveSections}
            />
          }
        />
      </FormProvider>
    </>
  );
};

MassiveSMSComponents.propTypes = {
  handleChangeSection: PropTypes.func.isRequired,
  userRoles: PropTypes.array,
};

export default MassiveSMSComponents;
